import React, { createContext, useContext, useEffect, ReactNode } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { useFilterService } from '../hooks';
import {
  appFiltersSelector,
  searchToggleSelector,
} from '../redux/selectors/filtersSelector';
import {
  setCurrentFilters,
  removeFilter,
  addFilter,
} from '../redux/sliceCreators/filtersSlice';
import { FilterData } from '../types';
import { Facet, FacetValue } from '../types/apiResponse';
import { FiltersContextProps } from '../types/filterContext';

export const filterParentPostfix = '_parent';

interface FiltersContextProviderProps {
  children: ReactNode;
}

const FiltersContextProvider = (props: FiltersContextProviderProps) => {
  const dispatch = useDispatch();
  const appFilters = useSelector(appFiltersSelector);
  const searchToggle = useSelector(searchToggleSelector);
  const filterService = useFilterService({ appFilters });

  useEffect(() => {
    const appFiltersIds = Object.values(appFilters).map(
      (x: FilterData) => x.id,
    );
    filterService.setFiltersToUrl(appFiltersIds, searchToggle);
  }, [appFilters, searchToggle]);

  const toggleFilter = (filter: FilterData) => {
    const isFilterSelected = Boolean(appFilters[filter.id]);
    if (isFilterSelected) {
      dispatch(removeFilter(filter));
    } else {
      dispatch(addFilter(filter));
    }
  };

  const setGlobalFilters = (filteredFilters: Facet[] = []) => {
    filteredFilters.forEach((filterField: Facet) => {
      if (!filterField.values) {
        filterField.values = [];
      }
      filterField.values?.forEach((filter: FacetValue) => {
        filterService.injectFilterData(filter, filterField.field);
      });
    });

    const finalFilters = filteredFilters?.map((item: Facet) => ({
      ...item,
      label: item?.label + filterParentPostfix,
    }));
    dispatch(setCurrentFilters(finalFilters));
  };

  return (
    <FiltersContext.Provider
      value={{
        ...filterService,
        toggleFilter,
        setGlobalFilters,
      }}
    >
      {props.children}
    </FiltersContext.Provider>
  );
};

const FiltersContext = createContext<FiltersContextProps>({
  getFiltersByField: () => [],
  getFilterById: () => undefined,
  getFiltersByPropertyValue: () => [],
  injectFilterData: () => {},
  getFilterId: () => '',
  createFilter: () => ({ id: '', field: '', label: '', value: '' }),
  parseFilter: () => '',
  createFilterQuery: () => '',
  parseValueFilter: () => '',
  parseRangeFilter: () => ['', ''],
  getUrlWithoutFilters: () => '',
  setFiltersToUrl: () => {},
  encodeFilterId: () => undefined,
  createNewFilter: () => null,
  getNewFilters: () => [],
  toggleFilter: () => {},
  setGlobalFilters: () => {},
});

const useFiltersContext = () => useContext(FiltersContext);

export { FiltersContextProvider, useFiltersContext };
